/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { useMediaQuery } from '@material-ui/core';
import {
    array, bool, func, object, string,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { GRAPHQL_ENV } from '../../../gql';
import Product from './Product';
import Frequency from './FCDContainer';
import OrderSummary from './OrderSummary';
import RecipientAddress from './RecipientAddress';
import SubscriptionGenericLoader from './ProductsGenericLoader';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import findProductPageByPartNumber from '../../../gql/queries/findProductPageByPartNumber';

// GA Tagging
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import Footer from './Partials/Footer';
import { isEmpty } from '../../../pages/Account/utils/object';

const useStyles = makeStyles({
    container: {
        padding: '10px 0 50px',
        textAlign: 'center',
    },
    breadCrumbs: {
        margin: 'auto',
        maxWidth: '1400px',
        padding: '0 40px',
        textAlign: 'left !important',
        '@media screen and (max-width: 768px)': {
            padding: '0 10px',
        },
    },
    title: ({ cmsStyles }) => ({
        fontSize: cmsStyles?.page_title?.desktop?.font_size,
        '@media screen and (max-width: 768px)': {
            fontSize: cmsStyles?.page_title?.mobile?.font_size,
        },
    }),
    error: {
        textAlign: 'center',
        fontSize: '1.5rem',
        padding: '100px 0',
    },
});

const Subscription = ({
    subscriptionData, brand, showBreadcrumbs, breadCrumb, disclaimerData, trackEvent, redirectSubscriptionPage, pathname,
}) => {
    const cmsStyles = subscriptionData?.style_collection;
    const classes = useStyles({ cmsStyles });

    const [selectedVariant, setSelectedVariant] = useState('');
    const [selectedSku, setSelectedSku] = useState({});
    const [addToCartData, setAddToCartData] = useState({});
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const [selectedPrice, setSelectedPrice] = useState('');

    useEffect(() => {
        // track event for subscription page ab test
        if (!isEmpty(redirectSubscriptionPage)) {
            trackEvent({
                eventCategory: 'Test Impression',
                eventAction: 'Subscriptions LP',
                eventLabel: redirectSubscriptionPage.type || 'variant',
            });
        }
    }, [pathname]);

    const isMobile = useMediaQuery('(max-width: 768px)');
    let position = 'start';
    if (isMobile) position = 'center';

    // cms data
    const productContainerData = subscriptionData?.step_1 || {};
    const recipientContainerData = subscriptionData?.step_2 || {};
    const frequeContainerData = subscriptionData?.step_3 || {};

    const PRODUCT_BY_PART = findProductPageByPartNumber(brand, productContainerData?.product_sku, null);
    const { loading, error, data } = useQuery(PRODUCT_BY_PART);
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: PRODUCT_BY_PART,
            component: 'SubscriptionPage.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }
    let passportProducts = {};
    if (data) passportProducts = data?.findProductPageByPartNumber?.product;
    if (!loading && isEmpty(passportProducts)) return <div className={classes.error}>Subscription not found</div>;

    const scrollDownToSection = (id, behavior = 'smooth') => {
        if (typeof window !== 'undefined' && typeof document !== 'undefined' && id) {
            if (document.getElementById(id)) {
                const section = document.getElementById(id);
                if (typeof section.scrollIntoView === 'function') section.scrollIntoView({ behavior, block: position });
            }
        }
    };

    const handleTrackEvent = (ec, ea, el) => {
        trackEvent({
            eventCategory: ec || 'Subscriptions Exp',
            eventAction: ea || 'click – button',
            eventLabel: el || 'Continue',
        });
    };

    const selectVariant = (selectedItem, variant, price) => {
        setSelectedSku(selectedItem);
        setSelectedVariant(variant);
        setSelectedPrice(price);
        scrollDownToSection('recipient_container');
    };

    return (
        <div className={classes.container}>
            {showBreadcrumbs && (<div className={classes.breadCrumbs}><GraphqlBreadcrumbs breadCrumbArray={breadCrumb} /></div>)}
            <h1 className={classes.title}>{subscriptionData?.title}</h1>
            {loading ? <SubscriptionGenericLoader />
                : (
                    <Product
                        trackEvent={handleTrackEvent}
                        data={productContainerData}
                        product={passportProducts}
                        selectedVariant={selectedVariant}
                        selectVariant={selectVariant}
                        selectedSku={selectedSku}
                        setSelectedSku={setSelectedSku}
                        setProductSkuPrices={setProductSkuPrices}
                        productSkuPrices={productSkuPrices}
                    />
                )}
            <RecipientAddress
                scrollDownToSection={scrollDownToSection}
                data={recipientContainerData}
                trackEvent={handleTrackEvent}
                selectedSku={selectedSku}
                addToCartData={addToCartData}
                setAddToCartData={setAddToCartData}
            />
            <Frequency
                scrollDownToSection={scrollDownToSection}
                frequeContainerData={frequeContainerData}
                selectedSku={selectedSku}
                trackEvent={handleTrackEvent}
                product={passportProducts}
                addToCartData={addToCartData}
                setAddToCartData={setAddToCartData}
            />
            <OrderSummary
                brand={brand}
                trackEvent={handleTrackEvent}
                product={passportProducts}
                selectedVariant={selectedVariant}
                addToCartData={addToCartData}
                selectedPrice={selectedPrice}
                variantsDetail={productContainerData?.product_variants || {}}
            />
            <Footer disclaimer={disclaimerData} />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

const mapStateToProps = (state) => ({
    redirectSubscriptionPage: state?.ui?.abTesting?.redirect_subscription_page_ui || {},
});

Subscription.propTypes = {
    brand: string.isRequired,
    subscriptionData: object.isRequired,
    breadCrumb: array.isRequired,
    showBreadcrumbs: bool.isRequired,
    disclaimerData: string.isRequired,
    trackEvent: func.isRequired,
    redirectSubscriptionPage: object.isRequired,
    pathname: string.isRequired,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Subscription);
